const SUBSCRIPT_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

// Elements that exist as two-atom molecules (H₂, O₂, Cl₂…) when pure.
export const DIATOMIC_ELEMENTS = Object.freeze(['H', 'N', 'O', 'F', 'Cl', 'Br', 'I']);

/**
 * Counts the atoms in a plain-text formula such as "Fe2O3", "Ca(OH)2" or "P4O10".
 * Returns { Fe: 2, O: 3 }.
 */
export function parseFormula(formula) {
  const stack = [{}];
  const tokenPattern = /([A-Z][a-z]?)(\d*)|(\()|(\))(\d*)/g;
  let match;
  let consumed = 0;
  while ((match = tokenPattern.exec(formula)) !== null) {
    if (match.index !== consumed) break;
    consumed = tokenPattern.lastIndex;
    const [, symbol, count, openParen, closeParen, groupCount] = match;
    if (symbol) {
      const counts = stack[stack.length - 1];
      counts[symbol] = (counts[symbol] ?? 0) + Number(count || 1);
    } else if (openParen) {
      stack.push({});
    } else if (closeParen) {
      const group = stack.pop();
      const counts = stack[stack.length - 1];
      Object.entries(group).forEach(([groupSymbol, groupAtoms]) => {
        counts[groupSymbol] = (counts[groupSymbol] ?? 0) + groupAtoms * Number(groupCount || 1);
      });
    }
  }
  if (consumed !== formula.length || stack.length !== 1) {
    throw new Error(`Cannot read the formula "${formula}".`);
  }
  return stack[0];
}

/**
 * "Fe2O3" -> "Fe₂O₃" for display.
 */
export function formatFormula(formula) {
  return formula.replace(/\d/g, (digit) => SUBSCRIPT_DIGITS[Number(digit)]);
}

/**
 * How a pure element is written in an equation: "O2" for oxygen, "Fe" for iron.
 */
export function getElementMoleculeFormula(symbol) {
  return DIATOMIC_ELEMENTS.includes(symbol) ? `${symbol}2` : symbol;
}

function greatestCommonDivisor(first, second) {
  return second === 0 ? Math.abs(first) : greatestCommonDivisor(second, first % second);
}

/**
 * Simplest formula for a binary compound: a cation with charge +cationCharge and an anion
 * with charge -anionCharge. buildBinaryFormula('Al', 3, 'O', 2) -> "Al2O3".
 */
export function buildBinaryFormula(cationSymbol, cationCharge, anionSymbol, anionCharge) {
  const divisor = greatestCommonDivisor(cationCharge, anionCharge);
  const cationCount = anionCharge / divisor;
  const anionCount = cationCharge / divisor;
  return `${cationSymbol}${cationCount > 1 ? cationCount : ''}${anionSymbol}${anionCount > 1 ? anionCount : ''}`;
}
